import Vue from 'vue'
import axios from '@axios'
import moment from 'moment'
import useJwt from '@/auth/jwt/useJwt'

const getInitialState = () => ({
  filter: {
    storeId: null,
    pickupPoint: null,
    origin: [],
  },
  webSocket: {
    preparation: {
      config: null,
      ready: false,
      connected: false,
      ws: null,
      currentChannelId: null,
    },
  },
  allOrders: [],
  lastFetch: null,
  orderItems: {},
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    ready(state) {
      return state.webSocket.preparation.ready
    },
    preparationWebSocketConnected(state) {
      return state.webSocket.preparation.connected
    },
    orders(state) {
      return state.allOrders // Filter as needed by status/origin
    },
  },

  mutations: {
    SET_PREPARATION_READY(state, ready) {
      state.webSocket.preparation.ready = ready
    },
    SET_PREPARATION_WEBSOCKET_CONNECTED(state, connected) {
      state.webSocket.preparation.connected = connected
    },
    SET_FILTER(state, filter) {
      state.filter = filter
    },
    SET_ALL_ORDERS(state, orders) {
      state.allOrders = orders
    },
    SET_LAST_FETCHED(state) {
      state.lastFetch = moment()
    },
    CLEAN_STATE(state) {
      const defaulData = getInitialState()
      Object.keys(defaulData).forEach(key => {
        state[key] = defaulData[key]
      })
    },
    SET_PREPARATION_WS_CONFIG(state, data) {
      state.webSocket.preparation.config = data
    },
    SET_OR_ADD_ORDER(state, orderPayload) {
      if (!orderPayload) return
      const existingOrderIndex = state.allOrders.findIndex(order => order.id === orderPayload.id)
      if (existingOrderIndex !== -1) {
        const existingOrder = state.allOrders[existingOrderIndex]
        Vue.set(state.allOrders, existingOrderIndex, {
          ...existingOrder,
          ...orderPayload,
        })
      } else {
        state.allOrders.push(orderPayload)
      }
    },
  },

  actions: {
    async fetchPreparationWebSocketConfig({ commit }) {
      const { data } = await axios.get('/api/settings/delivery/ws')
      commit('SET_PREPARATION_WS_CONFIG', data)
    },

    async fetchPreparationOrders({ commit, state }) {
      const { storeId, pickupPoint } = state.filter
      if (!storeId) return
      const { data } = await axios.get(`/api/sales/preparation/store/${storeId}`, {
            params: {
              pickupPoint: pickupPoint ? 'DriveThru' : 'Counter',
              pageSize: 0,
              pageIndex: 9999,
              sortBy: 'PreparationStatusDate ASC',
            },
        })
        // console.log('Fetched preparation orders', data)
      commit('SET_LAST_FETCHED')
      commit('SET_ALL_ORDERS', data.results)
    },

    setFilter({ commit }, filter) {
      commit('SET_FILTER', filter)
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },

    connectToPreparationWebSocket({ commit, state, dispatch }) {
      const { webSocketEndpoint } = state.webSocket.preparation.config
      console.debug('[preparation websocket] Connecting to', webSocketEndpoint)
      state.webSocket.preparation.ws = new WebSocket(`${webSocketEndpoint}?Token=${useJwt.getToken()}`)
      state.webSocket.preparation.ws.onerror = error => {
        commit('SET_PREPARATION_READY', true)
        commit('SET_PREPARATION_WEBSOCKET_CONNECTED', false)
        console.debug('[preparation websocket] Connection error', error)
      }
      state.webSocket.preparation.ws.onopen = () => {
        console.debug('[preparation websocket] Connection opened')
        dispatch('joinPreparationChannel')
        commit('SET_PREPARATION_READY', true)
      }
      state.webSocket.preparation.ws.onmessage = event => {
        const data = JSON.parse(event.data)
        dispatch('handleIncomingPreparationEvent', data)
      }
      state.webSocket.preparation.ws.onclose = () => {
        console.debug('[preparation websocket] Connection closed')
        commit('SET_PREPARATION_WEBSOCKET_CONNECTED', false)
        dispatch('connectToPreparationWebSocket')
      }
    },

    async joinPreparationChannel({ commit, state }) {
      if (state.webSocket.preparation.ws) {
        if (state.webSocket.preparation.currentChannelId) {
          const message = {
            action: 'leave',
            channelId: state.webSocket.preparation.currentChannelId,
          }
          console.debug('[preparation websocket] Leaving channel ', message.channelId)
          state.webSocket.preparation.ws.send(JSON.stringify(message))
        }
        if (state.filter.storeId) {
          const message = {
            action: 'join',
            channelId: `store-${state.filter.storeId}-sale-preparation-events`,
          }
          console.debug('[preparation websocket] Joining channel ', message.channelId)
          state.webSocket.preparation.ws.send(JSON.stringify(message))
          commit('SET_PREPARATION_WEBSOCKET_CONNECTED', true)
          state.webSocket.preparation.currentChannelId = message.channelId
        } else {
          commit('SET_PREPARATION_WEBSOCKET_CONNECTED', false)
        }
      }
    },

    handleIncomingPreparationEvent({ commit, state }, data) {
      console.debug('[preparation websocket] receiving data', data)
      if (state.webSocket.preparation.currentChannelId !== data.channelId) {
        console.debug('[preparation websocket] received message from different channel, ignore', data)
        return
      }
      switch (data.action) {
        case 'sendMessage': {
          const orderEvent = JSON.parse(data.content)
          const expectedPickupPoint = state.filter.pickupPoint ? 'DriveThru' : 'Counter'
          const alreadyInState = state.allOrders.some(o => o.id === orderEvent.id)
          if (
            orderEvent.pickupPoint
            && orderEvent.pickupPoint !== expectedPickupPoint
            && !alreadyInState
          ) {
            console.debug(
              '[preparation websocket] skipping order, pickupPoint mismatch',
              orderEvent.pickupPoint,
              expectedPickupPoint,
            )
            break
          }
          commit('SET_OR_ADD_ORDER', orderEvent)
          commit('SET_LAST_FETCHED')
          break
        }
        default:
      }
    },

    async updatePreparationStatus({ dispatch }, { saleId, newStatus, print }) {
      await axios.put('/api/sales/preparation/update-status', {
        saleId,
        newStatus,
      })
      if (print) {
        const orderPrint = (await axios.get(`/api/sales/${saleId}`)).data
        try {
          this.busy = true
          await dispatch('pages/sale/order/printOrderExpedition', { orderPrint }, { root: true })
        } catch (err) {
          console.error('print:', err)
        } finally {
          this.busy = false
        }
      }
    },
  },
}
